import {
  Bell,
  Bot,
  Briefcase,
  Building2,
  CheckSquare,
  CircleDollarSign,
  Compass,
  CreditCard,
  Eye,
  FolderOpen,
  Globe,
  HardDrive,
  Inbox,
  Layers,
  ListChecks,
  ListTree,
  Mail,
  Network,
  Puzzle,
  ShieldCheck,
  Target,
  Upload,
  UserPlus,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * Sidebar lateral da area de Configuracoes. 3 itens ativos hoje
 * (Grupos de acao, Tipos de acao, Etapas), restante em "Em breve" para
 * sinalizar o roadmap sem virar dead end.
 *
 * Decisao: items "Em breve" ficam visiveis mas desabilitados. Mostra ao
 * usuario o que ainda vai chegar e evita re-trabalho de UI quando cada
 * item ganhar pagina. Caso prefira esconder, basta filtrar por `available`.
 */

interface ConfigNavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  available: boolean;
}

const ITEMS: ConfigNavItem[] = [
  {
    href: '/escritorio/configuracoes/dados-escritorio',
    label: 'Dados do escritório',
    icon: Building2,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/assinatura',
    label: 'Assinatura',
    icon: CreditCard,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/usuarios',
    label: 'Usuários',
    icon: Users,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/ia',
    label: 'Inteligência Artificial',
    icon: Bot,
    available: false,
  },
  {
    href: '/escritorio/configuracoes/termos',
    label: 'Alertas por palavra',
    icon: ListChecks,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/financeiro',
    label: 'Financeiro',
    icon: CircleDollarSign,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/categorias-documentos',
    label: 'Categorias de modelos',
    icon: FolderOpen,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/categorias-tarefas',
    label: 'Tarefas',
    icon: CheckSquare,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/grupos-de-acao',
    label: 'Grupos de ação',
    icon: Layers,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/tipos-de-acao',
    label: 'Tipos de ação',
    icon: ListTree,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/etapas',
    label: 'Etapas',
    icon: Workflow,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/certificado-digital',
    label: 'Certificado digital',
    icon: ShieldCheck,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/monitoramentos',
    label: 'Monitoramentos',
    icon: Eye,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/conexoes-tribunais',
    label: 'Conexões com Tribunais',
    icon: Network,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/metas',
    label: 'Metas',
    icon: Target,
    available: false,
  },
  {
    href: '/escritorio/configuracoes/origem-pessoas',
    label: 'Origem de pessoas',
    icon: UserPlus,
    available: false,
  },
  {
    href: '/escritorio/configuracoes/parceiros',
    label: 'Parceiros',
    icon: Briefcase,
    available: false,
  },
  {
    href: '/escritorio/configuracoes/caixa-entrada',
    label: 'Caixa de entrada',
    icon: Inbox,
    available: false,
  },
  {
    href: '/escritorio/configuracoes/notificacoes',
    label: 'Notificações',
    icon: Bell,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/email',
    label: 'E-mail de envio',
    icon: Mail,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/importacao',
    label: 'Importação de dados',
    icon: Upload,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/armazenamento',
    label: 'Armazenamento',
    icon: HardDrive,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/customizacoes',
    label: 'Customizações',
    icon: Puzzle,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/tour',
    label: 'Tour do sistema',
    icon: Compass,
    available: true,
  },
  {
    href: '/escritorio/configuracoes/integracoes',
    label: 'Integrações e API',
    icon: Globe,
    available: false,
  },
];

/** Itens com data-tour: ancoram o tour de Configurações (rodada 3). */
const TOUR_TARGET_BY_HREF: Record<string, string> = {
  '/escritorio/configuracoes/dados-escritorio': 'config-dados-escritorio',
  '/escritorio/configuracoes/usuarios': 'config-usuarios',
  '/escritorio/configuracoes/assinatura': 'config-assinatura',
};

export function ConfigSidebar() {
  return (
    <aside
      data-tour="config-sidebar"
      className="w-full shrink-0 md:w-60"
      aria-label="Configurações"
    >
      <nav>
        <ul className="flex flex-col gap-0.5">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            const tourTarget = TOUR_TARGET_BY_HREF[item.href];
            if (!item.available) {
              return (
                <li key={item.href}>
                  <span
                    title="Em breve"
                    aria-disabled="true"
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm',
                      'text-[var(--fg-muted)] opacity-50 cursor-not-allowed select-none',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70">
                      em breve
                    </span>
                  </span>
                </li>
              );
            }
            return (
              <li key={item.href}>
                <NavLink
                  to={item.href}
                  data-tour={tourTarget}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                      'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                      isActive &&
                        'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className="absolute inset-y-1 left-0 w-0.5 rounded-r-full bg-[var(--accent)]"
                          aria-hidden
                        />
                      )}
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
